import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Restaurant as CaptainIcon,
  LocalDining as WaiterIcon,
  PointOfSale as BillingIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { userService } from '../services/userService';
import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { colors } from '../theme/theme';
import toast from 'react-hot-toast';

const Users = () => {
  const navigate = useNavigate();
  const { branch: selectedBranch } = useAppStore();
  const authUser = useAuthStore((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [deletingItem, setDeletingItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [savingUser, setSavingUser] = useState(false);

  const [newUser, setNewUser] = useState({
    full_name: '',
    email: '',
    phone: '',
    role: 'waiter',
    password: '',
    branch: null,
  });

  const roles = [
    { value: 'captain', label: 'Captain', icon: <CaptainIcon /> },
    { value: 'waiter', label: 'Waiter', icon: <WaiterIcon /> },
    { value: 'billing', label: 'Billing Staff', icon: <BillingIcon /> },
  ];

  // Load users when branch changes
  useEffect(() => {
    if (selectedBranch && !selectedBranch.isAllOutlets) {
      loadUsers();
    } else {
      setUsers([]);
    }
  }, [selectedBranch]);

  const loadUsers = async () => {
    if (!selectedBranch || selectedBranch.isAllOutlets) {
      setError('Please select a specific branch to manage users');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await userService.getUsers({
        branch_id: selectedBranch.id,
      });

      if (response?.data?.success) {
        setUsers(response.data.data || []);
      } else {
        setError(response?.data?.message || 'Failed to load users');
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (user = null) => {
    setError('');
    if (user) {
      setEditingUser(user);
      setNewUser({
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || 'waiter',
        password: '',
        branch: user.branch,
      });
    } else {
      setEditingUser(null);
      setNewUser({
        full_name: '',
        email: '',
        phone: '',
        role: 'waiter',
        password: '',
        branch: selectedBranch?.id,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingUser(null);
    setNewUser({
      full_name: '',
      email: '',
      phone: '',
      role: 'waiter',
      password: '',
      branch: null,
    });
  };

  const handleSave = async () => {
    if (!selectedBranch || selectedBranch.isAllOutlets) {
      toast.error('Please select a specific branch before creating a user');
      return;
    }

    if (!newUser.full_name.trim() || !newUser.email.trim()) {
      toast.error('Full name and email are required');
      return;
    }

    if (!editingUser && !newUser.password.trim()) {
      toast.error('Password is required for new users');
      return;
    }

    if (!['captain', 'waiter', 'billing'].includes(newUser.role)) {
      toast.error('Please choose a valid user role');
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      toast.error('Authentication required. Please log in again.');
      window.location.href = '/login';
      return;
    }

    setSavingUser(true);
    try {
      const userData = {
        full_name: newUser.full_name.trim(),
        email: newUser.email.trim(),
        phone: newUser.phone?.trim() || '',
        branch: selectedBranch.id,
      };

      const canEditRole = authUser?.role === 'superadmin';
      if (!editingUser || canEditRole) {
        userData.role = newUser.role;
      }

      if (!editingUser) {
        userData.password = newUser.password.trim();
      }

      let response;
      if (editingUser) {
        response = await userService.updateUser(editingUser.id, userData);
      } else {
        response = await userService.createUser(userData);
      }

      if (response?.data?.success) {
        toast.success(editingUser ? 'User updated successfully' : 'User created successfully');
        await loadUsers();
        handleClose();
        return;
      }

      const message = response?.data?.message || 'Failed to save user';
      const errorDetail = response?.data?.errors ? JSON.stringify(response.data.errors) : null;
      toast.error(errorDetail ? `${message}: ${errorDetail}` : message);
    } catch (err) {
      console.error('Error saving user:', err);
      const message = err?.response?.data?.message || err.message || 'Failed to save user';
      const errorDetail = err?.response?.data?.errors ? JSON.stringify(err.response.data.errors) : null;
      toast.error(errorDetail ? `${message}: ${errorDetail}` : message);
    } finally {
      setSavingUser(false);
    }
  };

  const openDeleteConfirmation = (user) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmOpen(false);
    setUserToDelete(null);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) {
      closeDeleteConfirmation();
      return;
    }

    const userId = userToDelete.id;
    setDeletingItem(userId);
    try {
      const response = await userService.deleteUser(userId);
      if (response.status === 204 || response?.data?.success) {
        toast.success('User deleted successfully');
        await loadUsers();
        closeDeleteConfirmation();
      } else {
        toast.error(response?.data?.message || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Delete user error:', err);
      const message = err?.response?.data?.message || err.message || 'Failed to delete user';
      toast.error(message);
    } finally {
      setDeletingItem(null);
    }
  };

  const handleToggleStatus = async (userId) => {
    setDeletingItem(userId);
    try {
      const response = await userService.toggleUserStatus(userId);
      if (response?.data?.success) {
        const user = response.data.data;
        toast.success(`User ${user.is_active ? 'activated' : 'deactivated'} successfully`);
        await loadUsers();
      } else {
        toast.error(response?.data?.message || 'Failed to toggle user status');
      }
    } catch (err) {
      console.error('Toggle user status error:', err);
      toast.error('Failed to toggle user status');
    } finally {
      setDeletingItem(null);
    }
  };

  const getRoleIcon = (role) => {
    const roleData = roles.find(r => r.value === role);
    return roleData ? roleData.icon : <PersonIcon />;
  };

  const getRoleLabel = (role) => {
    const roleData = roles.find(r => r.value === role);
    return roleData ? roleData.label : role;
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'captain':
        return 'error';
      case 'waiter':
        return 'info';
      case 'billing':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };

  const activeUsers = users.filter(user => user.is_active).length;
  const totalUsers = users.length;

  const branchLabel = selectedBranch?.name || 'All Outlets';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#FAFAFA', p: { xs: 2, md: 3 } }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
          color: 'white',
          boxShadow: `0 8px 32px ${colors.primary.main}20`,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              User Management
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Branch: <strong>{branchLabel}</strong>
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{
              backgroundColor: 'white',
              color: colors.primary.main,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
            }}
            disabled={!selectedBranch || selectedBranch.isAllOutlets}
          >
            Add User
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {!selectedBranch || selectedBranch.isAllOutlets ? (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 2,
            backgroundColor: '#FFF3E0',
            border: '2px dashed #FF9800',
          }}
        >
          <Typography variant="h6" sx={{ color: '#E65100', fontWeight: 600, mb: 1 }}>
            Select a Branch
          </Typography>
          <Typography variant="body2" sx={{ color: '#E65100', mb: 2 }}>
            Please select a specific branch from the top navigation to manage users.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/dashboard')}
            sx={{ backgroundColor: '#FF9800' }}
          >
            Go to Dashboard
          </Button>
        </Paper>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  background: '#FFFFFF',
                }}
              >
                <CardContent>
                  <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                    Total Users
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary.main }}>
                    {totalUsers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  background: '#FFFFFF',
                }}
              >
                <CardContent>
                  <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                    Active Users
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                    {activeUsers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  background: '#FFFFFF',
                }}
              >
                <CardContent>
                  <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                    Inactive Users
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#F44336' }}>
                    {totalUsers - activeUsers}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  background: '#FFFFFF',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.875rem' }}>
                        Actions
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        —
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={loadUsers}
                      disabled={loading}
                      sx={{ color: colors.primary.main }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Users Table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : users.length === 0 ? (
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 2,
                backgroundColor: '#F5F5F5',
              }}
            >
              <PersonIcon sx={{ fontSize: 48, color: '#BDBDBD', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                No users found
              </Typography>
              <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
                No users have been added to this branch yet. Click "Add User" to create one.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpen()}
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
                }}
              >
                Add First User
              </Button>
            </Paper>
          ) : (
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F5F5F5', borderBottom: `2px solid ${colors.primary.main}` }}>
                      <TableCell sx={{ fontWeight: 700, color: colors.primary.main }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: colors.primary.main }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: colors.primary.main }}>Contact</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: colors.primary.main }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: colors.primary.main }}>Joined</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: colors.primary.main }} align="center">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow
                        key={user.id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': { backgroundColor: '#F9F9F9' },
                          borderBottom: '1px solid #EEEEEE',
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar
                              sx={{
                                background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
                                mr: 2,
                              }}
                            >
                              {user.full_name ? user.full_name.charAt(0).toUpperCase() : 'U'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {user.full_name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                {user.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getRoleIcon(user.role)}
                            <Chip
                              label={getRoleLabel(user.role)}
                              color={getRoleColor(user.role)}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{user.phone || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.is_active ? 'Active' : 'Inactive'}
                            color={getStatusColor(user.is_active)}
                            size="small"
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(user.date_joined).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleOpen(user)}
                            disabled={deletingItem === user.id}
                            sx={{ color: colors.primary.main }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(user.id)}
                            disabled={deletingItem === user.id}
                            title={user.is_active ? 'Deactivate' : 'Activate'}
                            sx={{ color: user.is_active ? '#FF9800' : '#4CAF50' }}
                          >
                            {user.is_active ? '✓' : '○'}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => openDeleteConfirmation(user)}
                            disabled={deletingItem === user.id}
                            sx={{ color: '#F44336' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
            color: 'white',
            fontWeight: 700,
          }}
        >
          {editingUser ? '✎ Edit User' : '+ Add New User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={newUser.full_name}
              onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
              disabled={savingUser}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              disabled={savingUser || !!editingUser}
              helperText={editingUser ? 'Email cannot be changed' : ''}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={newUser.phone}
              onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
              disabled={savingUser}
              variant="outlined"
            />
            <FormControl fullWidth disabled={savingUser || (editingUser && authUser?.role !== 'superadmin')}>
              <InputLabel>Role</InputLabel>
              <Select
                value={newUser.role}
                label="Role"
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              >
                {roles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {role.icon}
                      <Typography>{role.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {editingUser && authUser?.role !== 'superadmin' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Only super admin can change role after creation.
                </Typography>
              )}
            </FormControl>
            {!editingUser && (
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                disabled={savingUser}
                helperText="Temporary password for the user"
                variant="outlined"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleClose} disabled={savingUser}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={savingUser}
            sx={{
              background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.secondary.main} 100%)`,
            }}
          >
            {savingUser ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {editingUser ? 'Update' : 'Add'} User
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={closeDeleteConfirmation} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{userToDelete?.full_name || 'this user'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirmation} disabled={deletingItem === userToDelete?.id}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteUser}
            variant="contained"
            color="error"
            disabled={deletingItem === userToDelete?.id}
          >
            {deletingItem === userToDelete?.id ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;